import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import DropList from 'components/atoms/DropList';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Label from 'components/atoms/Label';
import Message from 'components/atoms/Message';
import TypeTag from 'components/atoms/TypeTag';
import { YES_AND_NO_BOOLAN } from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import { SetGridSettingsAction, setGridSettingsAction } from 'store/actions/layoutActions';
import { OpenModal, RootState } from 'types';
import { GridColumnInterface, GridSettingsInterface } from 'types/functions';
import { ActionsType } from 'types/system';
import { clone, setKey } from 'utils/formHandlers';
import { translate } from 'utils/translate';

interface GridSettingModelProps {
    //unused props
    children?: string | JSX.Element | JSX.Element[];

    //unused props
    language: string;
    id?: string;

    //unused props
    data: any;
    actions: ActionsType | undefined;
    gridSettings: GridSettingsInterface;
    setGridSettingsAction: SetGridSettingsAction;
    openModelFunction: OpenModal;
}

interface GridSettingModelState {
    gridSettings: GridSettingsInterface;
}

class GridSettingModel extends Component<GridSettingModelProps, GridSettingModelState> {
    constructor(props: GridSettingModelProps) {
        super(props);
        this.state = {
            gridSettings: clone(props.gridSettings),
        };
    }

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.props.setGridSettingsAction(this.state.gridSettings);
        this.props.actions?.onCancel();
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    updateTagSetting = (type) => {
        this.setState((prevState) => {
            const { gridSettings } = prevState;
            const updatedTags = gridSettings.tags.includes(type)
                ? gridSettings.tags.filter((tag) => tag !== type)
                : [...gridSettings.tags, type];

            return {
                gridSettings: {
                    ...gridSettings,
                    tags: updatedTags,
                },
            };
        });
    };

    handleChange = (name, value) => {
        const { gridSettings } = this.state;
        setKey(gridSettings, name, value);
        this.setState({
            gridSettings,
        });
    };

    updateColumnsSetting = (column: GridColumnInterface) => {
        this.setState((prevState) => {
            const { gridSettings } = prevState;

            return {
                gridSettings: {
                    ...gridSettings,
                    columns: gridSettings.columns.map((x) => {
                        if (x.uuid === column.uuid) {
                            x.status = !x.status;
                        }
                        return x;
                    }),
                },
            };
        });
    };

    addGridColumn = () => {
        this.props.openModelFunction(
            'addGridColumnModel',
            0,
            'medium',
            {},
            {
                onCancel: () => {
                    this.props.openModelFunction('addGridColumnModel');
                },
                onConfirm: this.saveGridColumn,
            },
            true
        );
    };

    saveGridColumn = (column: GridColumnInterface, deleteColumn = false) => {
        const safeColumn = {
            ...column,
            label: typeof column.label === 'string' ? column.label.replace(/<[^>]*>/g, '') : column.label,
        };

        this.setState((prevState) => {
            const { gridSettings } = prevState;

            if (deleteColumn) {
                const updatedColumns = gridSettings.columns.filter((col) => col.uuid !== safeColumn.uuid);

                return {
                    gridSettings: {
                        ...gridSettings,
                        columns: updatedColumns,
                    },
                };
            }

            const existingColumnIndex = gridSettings.columns.findIndex((col) => col.uuid === safeColumn.uuid);

            if (existingColumnIndex !== -1) {
                const updatedColumns = [...gridSettings.columns];
                updatedColumns[existingColumnIndex] = safeColumn;

                return {
                    gridSettings: {
                        ...gridSettings,
                        columns: updatedColumns,
                    },
                };
            }

            return {
                gridSettings: {
                    ...gridSettings,
                    columns: [...gridSettings.columns, safeColumn],
                },
            };
        });
    };

    editGridColumn = (column) => {
        this.props.openModelFunction(
            'editGridColumnModel',
            0,
            'medium',
            { column: column },
            {
                onCancel: () => {
                    this.props.openModelFunction('editGridColumnModel');
                },
                onConfirm: this.saveGridColumn,
            },
            true
        );
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Grid settings')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>
                    {this.state.gridSettings.columns
                        .filter((x) => {
                            return x.status;
                        })
                        .reduce((sum, item) => sum + item.size, 0) > 22 && (
                        <Message className="mt-1" type="warning">
                            {translate(
                                'Some columns are too large to fit on the screen. Please remove them for a better display.'
                            )}
                        </Message>
                    )}

                    <FormGroup>
                        <Label>{translate('Grid type')}</Label>
                        <DropList
                            capitalize
                            name="type"
                            onChange={this.handleChange}
                            value={this.state.gridSettings.type}
                            options={[
                                { label: translate('default'), value: 'default' },
                                { label: translate('compact'), value: 'compact' },
                            ]}
                        ></DropList>
                    </FormGroup>
                    <FormGroup>
                        <Label>{translate('Short title')}</Label>
                        <DropList
                            capitalize
                            name="use_short_titles"
                            onChange={this.handleChange}
                            value={this.state.gridSettings.use_short_titles}
                            options={YES_AND_NO_BOOLAN}
                        ></DropList>
                    </FormGroup>
                    <FormGroup>
                        <Label>{translate('Columns')}</Label>
                        <div className="flex gap-2 flex-wrap">
                            {this.state.gridSettings.columns.map((x, i) => {
                                return (
                                    <div
                                        key={i}
                                        onClick={() => {
                                            if (x.key === 'regionArea') {
                                                this.editGridColumn(x);
                                            } else {
                                                this.updateColumnsSetting(x);
                                            }
                                        }}
                                    >
                                        <TypeTag className="first-letter-uppercase" active={x.status} type="user">
                                            {x.label}
                                        </TypeTag>
                                    </div>
                                );
                            })}

                            <div onClick={this.addGridColumn}>
                                <TypeTag active={true} type="user">
                                    {translate('Add custom column')}
                                </TypeTag>
                            </div>
                        </div>
                    </FormGroup>

                    <FormGroup>
                        <Label>{translate('Select tags  to display')}</Label>
                        <div className="flex gap-2">
                            <div
                                onClick={() => {
                                    this.updateTagSetting('user');
                                }}
                            >
                                <TypeTag active={this.state.gridSettings.tags.includes('user')} type="user">
                                    {translate('User')}
                                </TypeTag>
                            </div>
                            <div
                                onClick={() => {
                                    this.updateTagSetting('analysis');
                                }}
                            >
                                <TypeTag active={this.state.gridSettings.tags.includes('analysis')} type="analysis">
                                    {translate('Analysis')}
                                </TypeTag>
                            </div>
                            <div
                                onClick={() => {
                                    this.updateTagSetting('positioning');
                                }}
                            >
                                <TypeTag
                                    active={this.state.gridSettings.tags.includes('positioning')}
                                    type="positioning"
                                >
                                    {translate('Route')}
                                </TypeTag>
                            </div>
                            <div
                                onClick={() => {
                                    this.updateTagSetting('system');
                                }}
                            >
                                <TypeTag active={this.state.gridSettings.tags.includes('system')} type="system">
                                    {translate('System')}
                                </TypeTag>
                            </div>
                        </div>
                    </FormGroup>

                    <div className="flex justify-end">
                        <div className="mt-5 flex gap-3">
                            <Button
                                className="px-4 py-2 font-bold border border-gray-400"
                                onClick={this.handleFormCancel}
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button type="submit" className="px-4 py-2 bg-primary-submit rounded-md text-white">
                                {translate('Save')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}

const gridSettingModelStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    gridSettings: state.layout.gridSettings,
    openModelFunction: state.layout.openModelFunction,
});

const gridSettingModelDispatchToProps = (dispatch: Dispatch) => ({
    setGridSettingsAction: bindActionCreators(setGridSettingsAction, dispatch),
});

export default connect(gridSettingModelStateToProps, gridSettingModelDispatchToProps)(GridSettingModel);
